/**
 * Algolia Insights — manual event tracking.
 *
 * The search page and the search drawer query the Algolia REST API directly, so the
 * official Algolia Shopify pixel can't attribute anything on its own. Events sent:
 *
 *   clickedObjectIDsAfterSearch      — gdl-algolia-search.js / gdl-algolia-search-page.js
 *                                      call GdlAlgoliaInsights.trackClick()
 *   addedToCartObjectIDsAfterSearch  — here, from the /cart/add response
 *   purchasedObjectIDsAfterSearch    — custom web pixel, see
 *                                      docs/algolia-insights-custom-pixel.js
 *
 * Search clicks are stored in localStorage (`algolia_analytics_clicked_objects`) so a
 * later add-to-cart / purchase can be joined back to its queryID. The web pixel reads
 * the same store plus `algolia_config` (appId, search key, userToken).
 */
(function () {
  'use strict';

  const INSIGHTS_CDN = 'https://cdn.jsdelivr.net/npm/search-insights@2/dist/search-insights.min.js';
  const STORE_KEY    = 'algolia_analytics_clicked_objects';
  const CONFIG_KEY   = 'algolia_config';
  const MAX_RECORDS  = 50;
  const TTL_MS       = 30 * 24 * 60 * 60 * 1000; // Algolia attribution window

  // ─── Storage ──────────────────────────────────────────────────────────────────

  function read(key, fallback) {
    try { return JSON.parse(localStorage.getItem(key)) ?? fallback; }
    catch (e) { return fallback; }
  }

  function write(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  function records() {
    const now = Date.now();
    const list = read(STORE_KEY, []);
    return Array.isArray(list)
      ? list.filter(r => r && r.objectID && (!r.ts || now - r.ts < TTL_MS))
      : [];
  }

  function saveRecords(list) {
    write(STORE_KEY, list.slice(-MAX_RECORDS));
  }

  function currency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  }

  let insightsPromise = null;

  // ─── Insights library ─────────────────────────────────────────────────────────

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing._gdlLoaded) return resolve();
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', reject);
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.addEventListener('load', () => { script._gdlLoaded = true; resolve(); });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  /**
   * Loads + inits search-insights and persists the credentials and the anonymous
   * userToken for the web pixel. Credentials are optional on pages without a search
   * widget: they are read back from the stored config. Always resolves.
   */
  function ensureInsights(appId, apiKey, indexName) {
    const config = read(CONFIG_KEY, {}) || {};
    const id  = appId  || config.app_id;
    const key = apiKey || config.search_api_key;
    if (!id || !key) return Promise.resolve();

    if (appId && apiKey) {
      write(CONFIG_KEY, {
        ...config,
        app_id:         appId,
        search_api_key: apiKey,
        index_name:     indexName || config.index_name || '',
      });
    }

    if (insightsPromise) return insightsPromise;
    if (window.aa) {
      storeUserToken();
      insightsPromise = Promise.resolve();
      return insightsPromise;
    }

    // The CDN bundle only exposes itself as `window.aa` if this queue stub exists
    // before it loads (it registers as `window.AlgoliaAnalytics` otherwise).
    window.AlgoliaAnalyticsObject = 'aa';
    window.aa = window.aa || function () {
      (window.aa.queue = window.aa.queue || []).push(arguments);
    };

    insightsPromise = loadScript(INSIGHTS_CDN)
      .then(() => {
        window.aa('init', { appId: id, apiKey: key, useCookie: true });
        storeUserToken();
      })
      .catch(() => {});
    return insightsPromise;
  }

  function storeUserToken() {
    try {
      window.aa('getUserToken', {}, (err, userToken) => {
        if (err || !userToken) return;
        const config = read(CONFIG_KEY, {}) || {};
        if (config.user_token === userToken) return;
        write(CONFIG_KEY, { ...config, user_token: userToken });
      });
    } catch (e) {}
  }

  // ─── Click ────────────────────────────────────────────────────────────────────

  /**
   * @param {Object} opts - { index, objectID, handle, queryID, position, appId, apiKey }
   */
  function trackClick(opts) {
    const objectID = String(opts.objectID || '');
    if (!objectID) return;

    const record = {
      index:    opts.index || '',
      objectID,
      handle:   opts.handle || '',
      queryID:  opts.queryID || '',
      position: opts.position || 1,
      ts:       Date.now(),
    };
    saveRecords([...records().filter(r => r.objectID !== objectID), record]);

    if (!record.queryID) return;
    ensureInsights(opts.appId, opts.apiKey, record.index).then(() => {
      if (!window.aa) return;
      window.aa('clickedObjectIDsAfterSearch', {
        index:     record.index,
        eventName: 'Product Clicked',
        queryID:   record.queryID,
        objectIDs: [objectID],
        positions: [record.position],
      });
    });
  }

  // ─── Add to cart ──────────────────────────────────────────────────────────────

  /** @param {Object} item - line item as returned by /cart/add.js */
  function trackAddToCart(item) {
    const handle    = item.handle || '';
    const productId = item.product_id != null ? String(item.product_id) : '';
    const list      = records();
    const record    = list.find(r =>
      (handle && r.handle === handle) || (productId && r.productId === productId)
    );
    if (!record) return; // not clicked from a search → nothing to attribute

    // ponytail: /cart/add.js returns the line's cumulative quantity, so re-adding the
    // same variant reports the running total. Parse the request body if per-add
    // quantities ever need to be exact.
    const quantity = item.quantity || 1;
    const price    = (item.original_price ?? item.price ?? 0) / 100;
    const discount = Math.max(0, price - (item.final_price ?? item.price ?? 0) / 100);

    record.productId = productId;
    record.variantId = String(item.variant_id || item.id || '');
    record.ts        = Date.now();
    saveRecords(list);

    if (!record.queryID) return;
    ensureInsights().then(() => {
      if (!window.aa) return;
      window.aa('addedToCartObjectIDsAfterSearch', {
        index:      record.index,
        eventName:  'Product Added To Cart',
        queryID:    record.queryID,
        objectIDs:  [record.objectID],
        objectData: [{ price, quantity, discount }],
        value:      (price - discount) * quantity,
        currency:   currency(),
      });
    });
  }

  // Single hook for every add-to-cart path in the theme (product form, quick add,
  // highlighted product, size overlay) instead of patching each caller.
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const promise = originalFetch.apply(this, arguments);
    const url = typeof input === 'string' ? input : String((input && input.url) || input || '');
    if (!/\/cart\/add/.test(url)) return promise;

    return promise.then(response => {
      response.clone().json().then(data => {
        if (!data || (data.token && data.item_count != null)) return; // full cart, not an add
        const items = Array.isArray(data.items) ? data.items : (data.id ? [data] : []);
        items.forEach(trackAddToCart);
      }).catch(() => {});
      return response;
    });
  };

  window.GdlAlgoliaInsights = { ensureInsights, trackClick, records };
})();
